import { createHOC } from 'vue-hoc';

import ProfileForm from './ProfileForm';
import auth, { authResultKey } from '~lib/common/providers/auth';
import userManagement from '~lib/common/providers/userManagement';
import authConfig from '~lib/common/providers/auth.json';
import withOrgs from '~lib/common/hoc/withOrgs';
import withUser from '~lib/common/hoc/withUser';

const options = {
  name: 'ProfileFormContainer',
  props: {
    orgs: {
      type: Array,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      state: {
        loading: false,
        leavingOrg: false,
        user: this.getUser(),
        error: null,
        verificationEmailSent: false,
        invites: [],
      },
    };
  },
  async created() {
    if (!(await auth.isAuthenticated())) {
      this.$router.replace({ name: 'login' });
      return;
    }
    this.fetchInvites();
  },
  methods: {
    getUser() {
      const { idTokenPayload } = JSON.parse(localStorage.getItem(authResultKey));
      return {
        ...idTokenPayload,
        id: idTokenPayload.sub,
        org: idTokenPayload['https://vrpatients.com/org'],
        permissions: [],
      };
    },
    async fetchInvites() {
      try {
        const response = await userManagement.get(`invites/user/${this.state.user.sub}`);
        this.state.invites = response.data.invites;
      } catch (e) {
        this.state.error = e;
      }
    },
  }
};

const renderWith = {
  props: {
    loading() {
      return this.state.loading;
    },
    email() {
      return this.state.user.email;
    },
    givenName() {
      return this.state.user.given_name;
    },
    familyName() {
      return this.state.user.family_name;
    },
    loading() {
      return this.state.loading;
    },
    leavingOrg() {
      return this.state.leavingOrg;
    },
    error() {
      return this.state.error;
    },
    verificationEmailSent() {
      return this.state.verificationEmailSent;
    },
    invites() {
      return this.state.invites.filter(inv => !inv.declined);
    },
    user() {
      return this.user;
    },
    orgGroups() {
      return this.orgs;
    },
  },
  listeners: {
    async save(user) {
      this.state.error = null;
      this.state.loading = true;
      try {
        this.state.loading = true;
        this.state.verificationEmailSent = false;

        await userManagement.patch(`/users/${this.state.user.id}`, user);

        if (user.email) {
          await userManagement.post('/jobs/verification-email', {
            user_id: this.state.user.id,
            client_id: authConfig.clientId
          });
          this.state.verificationEmailSent = true;

          auth.logout();
          this.$router.replace({ name: 'login' });
        }
        else {
          await auth.renewSession();
          this.state.user = this.getUser();
        }
      } catch (e) {
        this.state.error = e;
      } finally {
        this.state.loading = false;
      }
    },
    async accept(inviteId) {
      // Add some status properties to the invite in reactive way
      const invite = this.state.invites.find(i => i._id === inviteId);
      if (!invite) {
        return;
      }
      this.$set(invite, 'loading', true);
      this.$set(invite, 'error', null);
      try {
        // Finish invitation
        await userManagement.post(
          '/invitations/finish-invite',
          { inviteId: invite._id, email: invite.email }
        );

        // Log into org
        await userManagement.post('/auth/changeOrg', {
          orgId: invite.org_id,
        });
        await auth.renewSession();
        window.location.reload();
      } catch (e) {
        invite.error = e.error;
      } finally {
        invite.loading = false;
      }
    },
    async dismiss(inviteId) {
      // Add some status properties to the invite in reactive way
      const invite = this.state.invites.find(i => i._id === inviteId);
      if (!invite) {
        return;
      }
      this.$set(invite, 'loading', true);
      this.$set(invite, 'error', null);
      try {
        await userManagement.post('/invitations/decline', { inviteId });
        invite.declined = true;
        this.$eventHub.$emit('INVITES::REFRESH');
      } catch (e) {
        invite.error = e.error;
      } finally {
        invite.loading = false;
      }
    },
    async leaveOrg(orgId) {
      const org = this.orgs.find(org => org._id === orgId);
      if (!org) {
        return;
      }
      this.$set(org, 'leaving', true);
      this.$set(org, 'leaveError', null);
      try {
        await userManagement.delete(`/extension/groups/${org._id}/members`, {},  {
          data: [this.user.id],
        });
        await auth.renewSession();
        window.location.reload();
      } catch (e) {
        org.leaveError = e;
      } finally {
        org.leaving = false;
      }
    },
    async fetchSummary(showAll) {
      try {
        const result = await userManagement.post(`account/scenario-summary?showAllOrgs=true&showAllUsers=${showAll}`);
        const blob = new Blob([result.data], {type: 'application/text'});
        const a = document.createElement('a')
        const blobURL = URL.createObjectURL(blob)
        a.download = 'summary.csv'
        a.href = blobURL
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      } catch(e) {
        this.state.error = e;
      }
    }
  },
};

const ProfileFormContainer = withUser()(withOrgs()(createHOC(
  ProfileForm,
  options,
  renderWith,
)));
export default ProfileFormContainer;
